"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import type { Locale } from "@/i18n/config";
import { cn } from "@/lib/cn";
import { TEXT_SIZE_KEY } from "@/lib/text-size";
import { Container, buttonStyles } from "./ui";
import { LanguageSwitcher } from "./language-switcher";

// O tamanho vive em <html data-text-size>, aplicado antes da pintura pelo
// script do layout; os dois botões (computador e celular) leem dali.
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function isLarge() {
  return document.documentElement.dataset.textSize === "large";
}

function setTextSize(large: boolean) {
  document.documentElement.dataset.textSize = large ? "large" : "normal";
  try {
    localStorage.setItem(TEXT_SIZE_KEY, large ? "large" : "normal");
  } catch {
    // Sem armazenamento: a escolha vale só nesta visita.
  }
  listeners.forEach((l) => l());
}

export type HeaderLabels = {
  cta: string;
  menu: string;
  close: string;
  textLarger: string;
  textNormal: string;
  language: string;
  mainNav: string;
  mobileNav: string;
};

function TextSizeToggle({
  labels,
  compact = false,
  className,
}: {
  labels: HeaderLabels;
  /** No cabeçalho do computador: só "AA" até 1536px; o nome vira dica e fica para leitores de tela. */
  compact?: boolean;
  className?: string;
}) {
  const large = useSyncExternalStore(subscribe, isLarge, () => false);
  const label = large ? labels.textNormal : labels.textLarger;

  return (
    <button
      type="button"
      onClick={() => setTextSize(!large)}
      aria-pressed={large}
      title={compact ? label : undefined}
      className={cn(
        "inline-flex min-h-12 cursor-pointer items-center gap-2 rounded-full border-2 px-4 text-base font-semibold whitespace-nowrap transition-colors",
        large
          ? "border-brand-950 bg-brand-950 text-white"
          : "border-brand-200 bg-white text-brand-950 hover:border-brand-600",
        className,
      )}
    >
      <span aria-hidden className="font-bold">
        A<span className="text-[1.3em]">A</span>
      </span>
      <span className={cn(compact && "sr-only 2xl:not-sr-only")}>{label}</span>
    </button>
  );
}

/** Recebe do servidor o logo e os textos já no idioma da página. */
export function HeaderClient({
  logo,
  locale,
  nav,
  ctaHref,
  labels,
}: {
  logo: React.ReactNode;
  locale: Locale;
  nav: { href: string; label: string }[];
  ctaHref: string;
  labels: HeaderLabels;
}) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 bg-white/95 backdrop-blur transition-shadow duration-300",
        (scrolled || open) && "shadow-[0_1px_0_rgb(0_26_61/0.08)]",
      )}
    >
      {/* Mais largo que o conteúdo da página: o menu completo precisa caber nos três idiomas. */}
      <Container className="flex h-20 max-w-7xl items-center justify-between gap-3">
        {logo}

        <nav aria-label={labels.mainNav} className="hidden xl:block">
          <ul className="flex items-center gap-1">
            {nav.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className="rounded-full px-3 py-2 text-lg font-medium whitespace-nowrap text-ink-soft transition-colors hover:bg-brand-50 hover:text-brand-950"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-2 xl:gap-3">
          <LanguageSwitcher locale={locale} label={labels.language} />
          <div className="hidden items-center gap-3 xl:flex">
            <TextSizeToggle labels={labels} compact />
            <Link
              href={ctaHref}
              className={cn(buttonStyles.primary, "min-h-12 px-6 whitespace-nowrap")}
            >
              {labels.cta}
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="menu-celular"
            className="inline-flex min-h-12 cursor-pointer items-center gap-2 rounded-full border-2 border-brand-200 bg-white px-4 text-base font-semibold text-brand-950 xl:hidden"
          >
            {open ? <X aria-hidden className="size-5" /> : <Menu aria-hidden className="size-5" />}
            <span className="max-[380px]:sr-only">{open ? labels.close : labels.menu}</span>
          </button>
        </div>
      </Container>

      {open && (
        <div id="menu-celular" className="border-t border-brand-100 xl:hidden">
          <Container className="py-5">
            <nav aria-label={labels.mobileNav}>
              <ul className="flex flex-col gap-1">
                {nav.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={() => setOpen(false)}
                      className="block rounded-xl px-4 py-3.5 text-xl font-medium text-brand-950 hover:bg-brand-50"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mt-5 flex flex-col gap-3">
              <TextSizeToggle labels={labels} className="justify-center" />
              <Link href={ctaHref} onClick={() => setOpen(false)} className={buttonStyles.primary}>
                {labels.cta}
              </Link>
            </div>
          </Container>
        </div>
      )}
    </header>
  );
}
